import React, { useState } from 'react';
import { ApiThumbnailSize, toApiThumbnailUrl } from '../utils/apiThumbnail';

// =====================================================================
// <ThumbnailImage>: rendert für kleine Karten/Avatare zuerst das
// Backend-Thumbnail (/api/file/thumbnails/…) und fällt bei einem
// Ladefehler automatisch aufs Original zurück (fehlende Thumbnails
// dürfen NIE ein leeres Bild erzeugen). Schlägt auch das Original fehl,
// greift der `onError`-Handler des Aufrufers (bestehendes Fallback-UI).
// Externe URLs (z. B. Flashscore) laufen unverändert durch.
// =====================================================================

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  /** Original-Bild-URL (absolut oder relativ). */
  src: string;
  /** Thumbnail-Größe (100/200/400) passend zur Render-Größe der Karte. */
  thumbSize: ApiThumbnailSize;
};

const ThumbnailImage: React.FC<Props> = ({ src, thumbSize, onError, alt, ...rest }) => {
  const thumb = toApiThumbnailUrl(src, thumbSize) ?? src;
  // Merken, WELCHES Thumbnail fehlgeschlagen ist: ändert sich `src`,
  // ändert sich auch `thumb` und der Versuch startet automatisch neu.
  const [failedThumb, setFailedThumb] = useState<string | null>(null);
  const useOriginal = thumb === src || failedThumb === thumb;

  const handleError: React.ReactEventHandler<HTMLImageElement> = (event) => {
    if (!useOriginal) {
      setFailedThumb(thumb); // Thumbnail fehlt → Original versuchen
      return;
    }
    onError?.(event); // Original kaputt → Fallback-UI des Aufrufers
  };

  const current = useOriginal ? src : thumb;
  const className = typeof rest.className === 'string' ? rest.className : '';
  // Marcel 13.10.2026: Karten-/Listenbilder (object-cover, keine runden Avatare) komplett sichtbar,
  // Ränder mit demselben Bild unscharf gefüllt – passt das Bild exakt, ist vom Blur nichts zu sehen.
  if (/\bobject-cover\b/.test(className) && !/\brounded-full\b/.test(className)) {
    const { className: _c, style, ...imgRest } = rest;
    const frame = className.replace(/\bobject-(cover|center|top|bottom|left|right)\b/g, '').replace(/\s+/g, ' ').trim();
    return (
      <span className={`relative block overflow-hidden ${frame}`} style={style} data-testid="blurred-img">
        <img src={current} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-md" loading="lazy"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <span className="absolute inset-0 bg-black/30" aria-hidden="true" />
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img src={current} alt={alt ?? ''} onError={handleError} className="relative h-full w-full object-contain" {...imgRest} />
      </span>
    );
  }

  // eslint-disable-next-line jsx-a11y/alt-text
  return <img src={current} alt={alt ?? ''} onError={handleError} {...rest} />;
};

export default ThumbnailImage;
